import { SECOND_MS, MINUTE_MS, HOUR_MS } from '@calm/web-common/dist/time';
import { jwtDecode } from 'jwt-decode';

export interface CircleJwtPayload {
    countryCode?: string;
    exp?: number;
    [key: string]: unknown;
}

/**
 * Safely decodes a circle authorization JWT and returns the parsed payload.
 * Returns undefined if the JWT is invalid or cannot be decoded.
 *
 * @param jwt - The circle authorization JWT string to decode
 * @returns The decoded JWT payload, or undefined if decoding fails
 *
 * @example
 * ```typescript
 * const jwt = circle.authorizationJWT;
 * const payload = getParsedCircleJwt(jwt);
 * const countryCode = payload?.countryCode;
 * ```
 */
export function getParsedCircleJwt(
    jwt: string | undefined
): CircleJwtPayload | undefined {
    if (!jwt) {
        return undefined;
    }

    try {
        return jwtDecode<CircleJwtPayload>(jwt);
    } catch (error) {
        console.error('Failed to decode circle JWT:', error);
        return undefined;
    }
}

/** Circle JWT lifetime issued by the circle API. Fixed at 30 minutes. */
export const CIRCLE_JWT_TTL_MS = 30 * MINUTE_MS;

/**
 * How far before JWT expiry the refetch timer fires and data is considered stale.
 * Keeping staleTime aligned to this value means React Query passive refetches
 * (window focus, mount) and the proactive timer in CircleAuthProvider both become
 * eligible at the same threshold — whichever fires first wins.
 *
 * Moved from CIRCLE_JWT_EXPIRATION_BUFFER in circle-auth/index.tsx.
 */
export const CIRCLE_JWT_EXPIRATION_BUFFER_MS = 30 * SECOND_MS;

/** A circle JWT with this little time left is treated as already expired. */
export const CIRCLE_JWT_EXPIRATION_BACKSTOP_MS = 1 * SECOND_MS;

/**
 * Milliseconds until a circle JWT expires; negative once it has. Returns
 * undefined for a missing, undecodable or `exp`-less JWT.
 */
export function getCircleJwtRemainingMs(
    jwt: string | undefined
): number | undefined {
    const exp = getParsedCircleJwt(jwt)?.exp;
    if (!exp) {
        return undefined;
    }
    return exp * SECOND_MS - new Date().getTime();
}

/**
 * Whether a circle JWT is expired, or within the hard-expiry backstop of it.
 * A missing, undecodable or `exp`-less JWT is not reported as expired.
 */
export function isCircleJwtExpired(jwt: string | undefined): boolean {
    const remainingMs = getCircleJwtRemainingMs(jwt);
    return (
        remainingMs !== undefined &&
        remainingMs <= CIRCLE_JWT_EXPIRATION_BACKSTOP_MS
    );
}

/**
 * Circle refresh token lifetime. Used as staleTime for the circles list query —
 * the list only needs refreshing when the refresh token itself would expire.
 */
export const CIRCLE_REFRESH_TOKEN_TTL_MS = 8 * HOUR_MS;
